import { t } from '@lingui/core/macro';
import { useCallback } from 'react';

import type { OptionType } from '@/api/common';
import type { OpportunityStatusType } from '@/api/opportunity';
import { useOpportunityStatusOptions } from '@/api/opportunity/opportunityAPI';
import {
  CategoryFilter,
  ComboboxPickerFilter,
  encodeFilterFieldName,
  FilterField,
  useFilterForm,
} from '@/components/filters';
import type { FilterOperatorMulti } from '@/components/InstantSearch';
import { OpportunityStatusTag } from '@/features/tags';
import type { FilterComponentProps } from '@/providers/TableState';

type Props = FilterComponentProps & {
  mode?: 'multiple';
};

const OpportunityStatusFilter = ({
  name,
  label,
  infoPopover,
  compact = false,
  mode = 'multiple',
  ...props
}: Props) => {
  const form = useFilterForm();
  const { opportunityStatusOptions } = useOpportunityStatusOptions();

  const renderOption = useCallback((option: OptionType) => {
    return <OpportunityStatusTag status={option.id as OpportunityStatusType} />;
  }, []);

  return (
    <form.AppField name={encodeFilterFieldName(name)}>
      {(field) => (
        <FilterField
          data-testid="opportunity-status-filter"
          label={label}
          infoPopover={infoPopover}
          hideLabel={!compact}
        >
          {compact ? (
            <ComboboxPickerFilter
              {...props}
              value={field.state.value as FilterOperatorMulti}
              onChange={field.handleChange}
              mode={mode}
              singlePlaceholder={t`Select Opportunity Status...`}
              multiplePlaceholder={t`Select Opportunity Statuses...`}
              multipleLoadingPlaceholder={t`Loading Opportunity Statuses...`}
              filteredItems={opportunityStatusOptions}
              renderOption={renderOption}
              notFoundContent={t`No opportunity statuses were found...`}
            />
          ) : (
            <CategoryFilter
              {...props}
              value={field.state.value as FilterOperatorMulti}
              onChange={field.handleChange}
              selectionMode={mode}
              options={opportunityStatusOptions}
              renderOption={renderOption}
            />
          )}
        </FilterField>
      )}
    </form.AppField>
  );
};

export default OpportunityStatusFilter;
